import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, Clock, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { api, formatApiErrorDetail } from "../lib/api";
import { useContent } from "../context/ContentContext";

export default function Contact() {
  const { content } = useContent();
  const { contact } = content;
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", city: "", service: "General Inquiry", yard_details: "", preferred_contact: "Phone" });
  const [sending, setSending] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please add your name, phone, and email.");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries({ ...form, address: form.address || "N/A", city: form.city || "N/A" }).forEach(([k, v]) => fd.append(k, v));
      await api.post("/quotes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", phone: "", email: "", address: "", city: "", service: "General Inquiry", yard_details: "", preferred_contact: "Phone" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSending(false);
    }
  };

  const cards = [
    { icon: Phone, label: "Call or Text", value: contact.phone },
    { icon: Mail, label: "Email", value: contact.email },
    { icon: Clock, label: "Hours", value: contact.hours },
    { icon: MapPin, label: "Serving", value: contact.areaLine },
  ];

  const inputCls = "w-full border-b-2 border-border bg-transparent px-0 py-3 focus:outline-none focus:border-emerald transition-colors rounded-none placeholder:text-olive/50 text-charcoal";

  return (
    <div data-testid="contact-page">
      <section className="bg-charcoal text-white py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><Eyebrow>Contact Us</Eyebrow></Reveal>
          <Reveal delay={1}>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-700 mt-5 max-w-3xl leading-[1.08] tracking-tight text-balance">
              Let's talk about your property.
            </h1>
          </Reveal>
          <Reveal delay={2}>
            <p className="text-white/75 text-lg mt-6 max-w-2xl leading-relaxed">
              Questions, scheduling, or a quick estimate — reach out and a real person from our family crew will respond.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {cards.map((c, i) => (
                <Reveal key={c.label} delay={i}>
                  <div className="bg-[#F8F9FA] border border-border rounded-sm p-6 h-full" data-testid={`contact-card-${i}`}>
                    <span className="grid place-items-center w-11 h-11 rounded-sm bg-emerald/10 text-emerald mb-4">
                      <c.icon className="w-5 h-5" />
                    </span>
                    <p className="text-xs tracking-[0.15em] uppercase text-gold font-600 mb-1">{c.label}</p>
                    <p className="text-charcoal font-500">{c.value}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={2}>
              <div className="mt-8 bg-emerald text-white rounded-sm p-8">
                <h3 className="font-display text-2xl font-600 mb-2">Proudly local.</h3>
                <p className="text-white/80 mb-4">We serve and live in these communities:</p>
                <div className="flex flex-wrap gap-2">
                  {contact.serviceAreas.map((a) => (
                    <span key={a} className="bg-white/10 border border-white/20 rounded-sm px-3 py-1.5 text-sm">{a}</span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={1}>
            <form onSubmit={submit} className="bg-white border border-border rounded-sm p-8 md:p-10 space-y-6" data-testid="contact-form">
              <h2 className="font-display text-2xl font-600 text-charcoal">Send us a message</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
                <input data-testid="contact-name" className={inputCls} placeholder="Name *" value={form.name} onChange={(e) => update("name", e.target.value)} />
                <input data-testid="contact-phone" className={inputCls} placeholder="Phone *" value={form.phone} onChange={(e) => update("phone", e.target.value)} />
              </div>
              <input data-testid="contact-email" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={(e) => update("email", e.target.value)} />
              <input data-testid="contact-city" className={inputCls} placeholder="City" value={form.city} onChange={(e) => update("city", e.target.value)} />
              <textarea data-testid="contact-message" rows={4} className={`${inputCls} resize-none`} placeholder="How can we help?" value={form.yard_details} onChange={(e) => update("yard_details", e.target.value)} />
              <button type="submit" disabled={sending} data-testid="contact-submit-btn" className="w-full inline-flex items-center justify-center gap-2 bg-emerald text-white px-8 py-4 rounded-sm font-600 hover:bg-emerald-dark transition-colors disabled:opacity-60">
                {sending ? <><Loader2 className="w-5 h-5 animate-spin" /> Sending…</> : <>Send Message <ArrowRight className="w-5 h-5" /></>}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
